import ButtonLink from '@components/ButtonLink'
import { tripHtmlFromBody } from '@utils/functions'
import { useMemo } from 'react'

/** Characters shown before a toast notification is expanded. */
export const NOTIFICATION_PREVIEW_LENGTH = 50

/**
 * Xero upload failures get a longer preview: their message puts what the user has to fix first and
 * the wording Xero itself used after it, so the preview has to clear the opening sentence — "Xero
 * contact <name> has no email address." — or the toast says nothing actionable on its own.
 *
 * It deliberately stops short of the whole instruction. Where "Xero said:" begins depends on the
 * contact's name, so a longer preview would leak those two words into some messages and read as if
 * the toast were broken. The rest is one click away behind "View Details".
 */
export const XERO_NOTIFICATION_PREVIEW_LENGTH = 120

type Props = {
  content: string
  expanded: boolean
  onToggle: () => void
  previewLength?: number
}

/**
 * Notification text that starts as a short preview. "View Details" expands it in place to the full
 * message and "Show less" folds it back. The button only shows when the preview cuts the text.
 */
const ExpandableNotificationContent = ({
  content,
  expanded,
  onToggle,
  previewLength = NOTIFICATION_PREVIEW_LENGTH,
}: Props) => {
  const fullText = useMemo(() => tripHtmlFromBody(content), [content])
  const preview = useMemo(
    () => tripHtmlFromBody(content, previewLength),
    [content, previewLength]
  )
  const isCut = fullText.length > previewLength

  return (
    <div>
      {expanded && isCut ? (
        <div className="whitespace-pre-line break-words max-h-96 overflow-auto">
          {fullText}
        </div>
      ) : (
        <div>{preview}</div>
      )}
      {isCut && (
        <ButtonLink className="text-xs" onClick={onToggle}>
          {expanded ? 'Show less' : 'View Details'}
        </ButtonLink>
      )}
    </div>
  )
}

export default ExpandableNotificationContent
